import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Field,
  Icon,
  IconTile,
  Modal,
  ModalActions,
  cn,
} from '@evogard/ui';
import type { UsefulLink } from '@evogard/contract';
import { usefulLinkUrl, type UsefulLinkIcon } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { LINK_ICONS, hostOf, useUsefulLinks } from './usefulLinks.js';

/**
 * O cadastro dos links úteis, dentro da Configuração da base (07/10/2026).
 *
 * Quem vê a configuração vê a lista; quem a edita (`platform.tenants:editar`)
 * cadastra, edita e remove. O que se cadastra aqui aparece em Plataforma →
 * Links úteis para todo mundo da base.
 */
export function UsefulLinksCard() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'platform.tenants', 'editar');
  const client = useQueryClient();
  const links = useUsefulLinks();
  /* `null` fechado; `'novo'` cadastrando; um link, editando aquele. */
  const [editing, setEditing] = useState<UsefulLink | 'novo' | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async (link: UsefulLink) => {
      const r = await api.usefulLinks.removeUsefulLink({
        params: { id: link.id },
        body: {},
      });
      return ok(r, 200, 'Não foi possível remover o link.');
    },
    onMutate: () => setRemoveError(null),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'links-uteis'] });
    },
    onError: (e: Error) => setRemoveError(e.message),
  });

  return (
    <Card
      title="Links úteis"
      descricao="Os atalhos que aparecem em Plataforma → Links úteis, para todo mundo da base."
      actions={
        canEdit && (
          <Button size="sm" icon="mais" onClick={() => setEditing('novo')}>
            Novo link
          </Button>
        )
      }
    >
      {links.isError && <Alert tone="erro">{links.error.message}</Alert>}
      {removeError && <Alert tone="erro">{removeError}</Alert>}

      {links.data?.length === 0 && (
        <p className="text-sm text-texto-suave">Nenhum link cadastrado nesta base.</p>
      )}

      {links.data && links.data.length > 0 && (
        <ul className="flex flex-col divide-y divide-borda-suave">
          {links.data.map((link) => (
            <li
              key={link.id}
              className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <IconTile icon={link.icon} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-texto">{link.title}</p>
                <p className="truncate text-xs text-texto-suave">{hostOf(link.url)}</p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="contorno" onClick={() => setEditing(link)}>
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="contorno"
                    loading={remove.isPending && remove.variables?.id === link.id}
                    onClick={() => remove.mutate(link)}
                  >
                    Remover
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== null && canEdit && (
        <UsefulLinkModal
          link={editing === 'novo' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

/** Cadastrar ou editar um link — o mesmo formulário para os dois. */
function UsefulLinkModal({
  link,
  onClose,
}: {
  link: UsefulLink | null;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [title, setTitle] = useState(link?.title ?? '');
  const [url, setUrl] = useState(link?.url ?? '');
  const [description, setDescription] = useState(link?.description ?? '');
  const [icon, setIcon] = useState<UsefulLinkIcon>(link?.icon ?? 'documento');
  const [error, setError] = useState<string | null>(null);

  /*
   * A mesma função que o contrato usa para normalizar: a tela avisa antes de
   * enviar, e o que ela aceita é exatamente o que a rota aceita.
   */
  const normalized = usefulLinkUrl(url);
  const urlProblem =
    url.trim() !== '' && normalized === null
      ? 'Informe o endereço de um site, como https://exemplo.com.br.'
      : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: title.trim(),
        url: url.trim(),
        description: description.trim() === '' ? null : description.trim(),
        icon,
      };
      if (link === null) {
        const r = await api.usefulLinks.createUsefulLink({ body });
        return ok(r, 201, 'Não foi possível cadastrar o link.');
      }
      const r = await api.usefulLinks.editUsefulLink({ params: { id: link.id }, body });
      return ok(r, 200, 'Não foi possível salvar o link.');
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'links-uteis'] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      tone="destaque"
      size="lg"
      icon={icon}
      title={link === null ? 'Novo link útil' : 'Editar link útil'}
      descricao="Aparece para todo mundo da base, em Plataforma → Links úteis."
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <Field
          label="Nome"
          required
          maxLength={80}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Como a operação chama o site"
        />

        <Field
          label="Endereço"
          required
          maxLength={2000}
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Ex.: https://exemplo.com.br"
          help={
            normalized && normalized !== url.trim()
              ? `Vai abrir ${normalized}`
              : undefined
          }
          error={urlProblem}
        />

        <Field
          label="Descrição"
          maxLength={160}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="—"
          help="Uma linha dizendo para que serve"
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="rotulo mb-2">Ícone</legend>
          <div className="grid grid-cols-6 gap-2 sm:grid-cols-9">
            {LINK_ICONS.map((name) => (
              <button
                key={name}
                type="button"
                aria-pressed={icon === name}
                aria-label={name}
                title={name}
                onClick={() => setIcon(name as UsefulLinkIcon)}
                className={cn(
                  'grid min-h-11 place-items-center rounded-borda border transition-[border-color,background-color]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
                  icon === name
                    ? 'border-primaria-texto bg-superficie-suave text-texto'
                    : 'border-borda text-texto-suave hover:border-texto-suave',
                )}
              >
                <Icon nome={name} size={20} />
              </button>
            ))}
          </div>
        </fieldset>

        <ModalActions className="border-t border-borda-suave pt-5">
          <Button
            type="submit"
            loading={save.isPending}
            disabled={!title.trim() || normalized === null}
          >
            {link === null ? 'Cadastrar' : 'Salvar'}
          </Button>
          <Button type="button" variant="contorno" onClick={onClose}>
            Cancelar
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
